import React, { useState, useCallback, useRef, useEffect } from 'react';
import { intl, LOCALES_FLAG, DmsButton, DmsTable, DmsTableDeleteButton, DmsButtonGroup, DmsFlexContainer, DmsSwitch, fetchData, getToken } from 'dmsbasereact';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Grid,
    TextField,
    MenuItem,
    Chip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';

/**
 * HelpCenterSettings - Help Center manual upload settings.
 * Allows uploading HTML/Markdown manuals for the help center.
 */
export const HelpCenterSettings = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const tableRef = useRef(null);
    const [slug, setSlug] = useState('');
    const [language, setLanguage] = useState('');
    const [targetRoles, setTargetRoles] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState(null);
    const [responseSeverity, setResponseSeverity] = useState('info');
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [allowDownload, setAllowDownload] = useState(true);

    // Convert LOCALES_FLAG to options array
    const languageOptions = Object.entries(LOCALES_FLAG || {}).map(([label, value]) => ({
        value,
        label
    }));

    // Fetch available roles on mount
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await fetchData('/help-center/roles', 'GET', true);
                if (response.data?.data) {
                    setAvailableRoles(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        };
        fetchRoles();
    }, []);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleFileButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleDownload = async (filePk, filename) => {
        const downloadUrl = `/help-center/file/${filePk}`;
        const token = getToken();
        try {
            const response = await fetch(downloadUrl, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token.access_token}` }
            });
            if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename || 'download';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            Swal.fire(intl.get('DownloadError').d('Download Error'), err.message, 'error');
        }
    };

    const handleUpload = useCallback(async () => {
        if (!selectedFile || !slug || !language) {
            setResponseMessage(
                intl.get('HelpCenterUpload.fieldsRequired').d('Por favor, selecione um arquivo e preencha o slug e o idioma.')
            );
            setResponseSeverity('error');
            return;
        }

        setLoading(true);
        setResponseMessage(null);

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('slug', slug);
        formData.append('language', language);
        formData.append('target_roles', targetRoles.join(','));
        formData.append('allow_download', allowDownload.toString());

        try {
            const response = await fetchData(
                '/help-center/manual_upload',
                'POST',
                true,
                formData,
                false,
                null,
                null,
                null,
                'multipart/form-data',
                false
            );

            if (response.status >= 200 && response.status < 300) {
                setResponseMessage(
                    intl.get('HelpCenterUpload.uploadSuccess').d('Manual enviado com sucesso!')
                );
                setResponseSeverity('success');
                setSelectedFile(null);
                setSlug('');
                setLanguage('');
                setTargetRoles([]);
                setAllowDownload(true);
                setShowUploadForm(false);
                if (tableRef.current) {
                    tableRef.current.updateTable();
                }
            } else {
                const errorDetail = response.data?.detail || response.data || response.statusText;
                setResponseMessage(
                    intl.get('HelpCenterUpload.uploadError', { errorDetail }).d('Erro ao enviar manual: {errorDetail}')
                );
                setResponseSeverity('error');
            }
        } catch (error) {
            setResponseMessage(
                intl.get('HelpCenterUpload.uploadFailed', { errorMessage: error.message }).d('Falha na requisição: {errorMessage}')
            );
            setResponseSeverity('error');
        } finally {
            setLoading(false);
        }
    }, [selectedFile, slug, language, targetRoles, allowDownload]);

    const tableHeader = [
        {
            label: intl.get('Title').d('Title'),
            column_name: 'title',
            sortable: true
        },
        {
            label: intl.get('Slug').d('Slug'),
            column_name: 'slug',
            sortable: true
        },
        {
            label: intl.get('Language').d('Language'),
            column_name: 'language',
            sortable: true
        },
        {
            label: intl.get('Version').d('Version'),
            column_name: 'version',
            sortable: true
        },
        {
            label: intl.get('TargetRoles').d('Perfis Alvo'),
            column_name: 'target_roles_display',
            sortable: false,
            element: (row) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {row.target_roles && row.target_roles.length > 0 ? (
                        row.target_roles.map((role) => (
                            <Chip key={role} label={role} size="small" />
                        ))
                    ) : (
                        <Chip label={intl.get('AllUsers').d('Todos')} size="small" color="primary" />
                    )}
                </Box>
            )
        },
        {
            label: intl.get('File').d('File'),
            column_name: 'file',
            sortable: false,
            function: (file) => file?.original_filename || '-'
        },
        {
            label: intl.get('AllowDownload').d('Permitir Download'),
            column_name: 'allow_download',
            sortable: true,
            element: (row) => (
                <Chip
                    label={row.allow_download
                        ? intl.get('Yes').d('Yes')
                        : intl.get('No').d('No')}
                    size="small"
                    color={row.allow_download ? 'success' : 'default'}
                />
            )
        },
        {
            label: intl.get('Actions').d('Actions'),
            column_name: 'actions',
            width: '120px',
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    {row.file_pk && (
                        <DmsButton
                            icon={<DownloadIcon />}
                            onClick={() => handleDownload(row.file_pk, row.file?.original_filename)}
                            title={intl.get('DownloadFile').d('Download File')}
                        />
                    )}
                    <DmsTableDeleteButton
                        table={tableRef}
                        title={intl.get('DeleteManual').d('Excluir Manual')}
                        text={intl.get('DeleteManualConfirm').d('Tem certeza que deseja excluir este manual?')}
                        url="/help-center/manual/"
                        row_pk={row.pk}
                    />
                </DmsButtonGroup>
            )
        }
    ];

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsButton
                    variant="primary"
                    icon={<AddIcon />}
                    onClick={() => setShowUploadForm(!showUploadForm)}
                    text={showUploadForm
                        ? intl.get('HideUploadForm').d('Ocultar Formulário')
                        : intl.get('AddManual').d('Adicionar Manual')}
                />
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    {intl.get('HelpCenterManualUpload').d('Central de Ajuda - Manuais')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {intl.get('HelpCenterManualUploadDescription').d(
                        'Gerencie os manuais da central de ajuda. Cada manual é identificado por um slug único, idioma e perfis alvo.'
                    )}
                </Typography>

                {showUploadForm && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                            {intl.get('UploadNewManual').d('Enviar Novo Manual')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label={intl.get('ManualSlug').d('Slug do Manual')}
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                    fullWidth
                                    required
                                    size="small"
                                    helperText={intl.get('ManualSlugHelp').d('Identificador único (ex: guia-inicial)')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label={intl.get('Language').d('Language')}
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    fullWidth
                                    required
                                    size="small"
                                >
                                    {languageOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    label={intl.get('TargetRoles').d('Perfis Alvo')}
                                    value={targetRoles}
                                    onChange={(e) => setTargetRoles(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                    fullWidth
                                    size="small"
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.length === 0 ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {intl.get('AllUsers').d('Todos')}
                                                    </Typography>
                                                ) : (
                                                    selected.map((value) => (
                                                        <Chip key={value} label={value} size="small" />
                                                    ))
                                                )}
                                            </Box>
                                        )
                                    }}
                                    helperText={intl.get('TargetRolesHelp').d('Deixe vazio para todos os usuários')}
                                >
                                    {availableRoles.map((role) => (
                                        <MenuItem key={role.value} value={role.value}>
                                            {role.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        accept=".html,.htm,.md,.markdown,.pdf"
                                        style={{ display: 'none' }}
                                    />
                                    <DmsButton
                                        onClick={handleFileButtonClick}
                                        icon={<FileUploadIcon />}
                                        text={selectedFile
                                            ? selectedFile.name
                                            : intl.get('SelectFile').d('Select File')}
                                        sx={{ width: '100%' }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DmsSwitch
                                    name="allow_download"
                                    label={intl.get('AllowDownload').d('Permitir Download')}
                                    checked={allowDownload}
                                    onChange={(name, value) => setAllowDownload(value === 'true' || value === true)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DmsButton
                                    icon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                    text={loading
                                        ? intl.get('Uploading').d('Uploading...')
                                        : intl.get('UploadManual').d('Enviar Manual')}
                                    onClick={handleUpload}
                                    disabled={!selectedFile || !slug || !language || loading}
                                    variant="primary"
                                />
                            </Grid>
                        </Grid>

                        {responseMessage && (
                            <Alert severity={responseSeverity} sx={{ mt: 2 }}>
                                {responseMessage}
                            </Alert>
                        )}
                    </Paper>
                )}

                <DmsTable
                    header={tableHeader}
                    managed={true}
                    needs_authentication={true}
                    ref={tableRef}
                    url="/help-center/table"
                    toolbar={toolbar}
                />
            </Paper>
        </Box>
    );
};
